import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@root/components/v3/button';
import { Calendar } from '@root/components/v3/calendar';
import { Input } from '@root/components/v3/input';
import { Popover, PopoverContent, PopoverTrigger } from '@root/components/v3/popover';

/* ------------------------------
   Helpers
------------------------------ */

function formatDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB'); // dd/mm/yyyy
}

function isExplicitEmpty(value) {
  return value === '' || value === null;
}

function normalizeToDate(value) {
  if (!value) return null;

  // Date object
  if (value instanceof Date && !isNaN(value)) {
    return value;
  }

  // Unix timestamp (seconds)
  if (typeof value === 'number' || /^\d+$/.test(value)) {
    const d = new Date(Number(value) * 1000);
    return isNaN(d) ? null : d;
  }

  // ISO date string: YYYY-MM-DD (from BE)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const d = new Date(`${value}T00:00:00`);
    return isNaN(d) ? null : d;
  }

  return null;
}

function toUnixUTC(date) {
  return Math.floor(date.getTime() / 1000);
}

/* ------------------------------
   Component
------------------------------ */

export function CommonDatePicker({ value, onChange, fromException = false, minDate, maxDate }) {
  const today = React.useMemo(() => new Date(), []);
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState(today);
  const [month, setMonth] = React.useState(today);
  const [inputValue, setInputValue] = React.useState(today);

  /* ------------------------------
     Prefill logic
  ------------------------------ */
  React.useEffect(() => {
    let rawValue;
    let hasExplicitEmpty = false;

    // STRICT rule: object value ONLY allowed for exception flow
    if (fromException) {
      rawValue = value?.requestForException?.expiry_date;
      if (value?.requestForException && isExplicitEmpty(rawValue)) {
        hasExplicitEmpty = true;
      }
    } else {
      rawValue = value;
      if (isExplicitEmpty(rawValue)) {
        hasExplicitEmpty = true;
      }
    }

    // Case 1: Explicitly empty → clear field
    if (hasExplicitEmpty) {
      setDate(null);
      setInputValue('');
      return;
    }
    // Case 2: Try to parse provided value
    const parsedDate = normalizeToDate(rawValue);
    // Case 3: Nothing provided → default to today
    const finalDate = parsedDate ?? today;

    setDate(finalDate);
    setMonth(finalDate);
    setInputValue(formatDate(finalDate));
  }, [value, fromException, today]);

  /* ------------------------------
     Normalize min / max
  ------------------------------ */
  const min = React.useMemo(() => normalizeToDate(minDate), [minDate]);
  const max = React.useMemo(() => normalizeToDate(maxDate), [maxDate]);

  // The months the calendar can show. Its dropdown otherwise ends with the current year, so no later date could be
  // picked (an exception expiring next year, say).
  const endMonth = React.useMemo(() => max ?? new Date(today.getFullYear() + 10, 11), [max, today]);

  const disabledDays = React.useMemo(() => {
    if (!min && !max) return undefined;

    return {
      ...(min ? { before: min } : {}),
      ...(max ? { after: max } : {}),
    };
  }, [min, max]);

  /* ------------------------------
     Date selection handler
  ------------------------------ */
  const handleDateChange = selectedDate => {
    if (!(selectedDate instanceof Date) || isNaN(selectedDate)) {
      setInputValue('');
      onChange?.('');
      return;
    }

    setDate(selectedDate);
    setMonth(selectedDate);
    setInputValue(formatDate(selectedDate));
    // 🔑 DIFFERENCE HERE
    if (fromException) {
      // Exception → unix timestamp
      onChange?.(toUnixUTC(selectedDate));
    } else {
      // Common → Date object
      onChange?.(selectedDate);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="relative flex gap-2">
        <Input
          value={inputValue}
          placeholder="dd/mm/yyyy"
          className="bg-background pr-10"
          onChange={e => {
            // Manual typing only updates input
            setInputValue(e.target.value);
          }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" className="absolute top-1/2 right-2 size-6 -translate-y-1/2">
              <CalendarIcon className="size-3.5" />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-auto overflow-hidden p-0" align="end" alignOffset={-8} sideOffset={10}>
            <Calendar
              mode="single"
              selected={date ?? undefined}
              month={month}
              captionLayout="dropdown"
              onMonthChange={setMonth}
              onSelect={d => {
                if (!d) return;
                handleDateChange(d);
                setOpen(false);
              }}
              disabled={disabledDays}
              startMonth={min ?? undefined}
              endMonth={endMonth}
              className="w-[250px]"
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
